import type {
  CarrierColumn,
  FinancialsCoverageRef,
  FinancialsTableResponse,
  PeriodColumn,
  PeriodColumns,
  PeriodKey,
} from "@platform/api";

/**
 * Pure derivations and label resolution for the Financials tab.
 *
 * Two layers:
 *
 * 1. Extractors — know the response shape, pull the values a calc needs
 *    out of the payload given a (coverageId, carrierId) pair.
 * 2. Calcs — pure arithmetic over plain numbers/booleans, no payload
 *    knowledge. Apply the spec's null rules internally so callers
 *    don't have to remember them.
 *
 * Variance and dollar savings render in carrier columns (against
 * current and the visible renewal/revised-renewal reference) and in
 * the renewal/revised-renewal period columns (a single "to current"
 * leg). The current column has no reference to compare against.
 */

export type CellPair = {
  toCurrent: number | null;
  toOther: number | null;
};

export type CarrierCellInputs = {
  value: number | null;
  current: number | null;
  other: number | null;
  isVirgin: boolean;
};

/**
 * Picks the "other" reference period for variance/savings against a
 * carrier value: revised renewal wins over renewal when both are
 * visible; either is skipped when hidden or when the period has no
 * recorded premium for the coverage. Returns null when neither
 * applies, in which case the carrier row only renders a `toCurrent`
 * leg.
 */
const pickOtherReference = (
  periods: PeriodColumns,
  coverageId: string,
): number | null => {
  if (!periods.revisedRenewal.hidden) {
    const value = periods.revisedRenewal.coverages[coverageId]?.annualPremium;
    if (value !== undefined) return value;
  }

  if (!periods.renewal.hidden) {
    const value = periods.renewal.coverages[coverageId]?.annualPremium;
    if (value !== undefined) return value;
  }

  return null;
};

export const extractCarrierCellInputs = (
  periods: PeriodColumns,
  coverage: FinancialsCoverageRef,
  carrier: CarrierColumn,
): CarrierCellInputs => {
  const carrierCell = carrier.coverages[coverage.id] as
    | { annualPremium?: number; note?: string }
    | undefined;
  const currentCell = periods.current.coverages[coverage.id] as
    | { annualPremium?: number }
    | undefined;

  return {
    value: carrierCell?.annualPremium ?? null,
    current: currentCell?.annualPremium ?? null,
    other: pickOtherReference(periods, coverage.id),
    isVirgin: coverage.isVirgin,
  };
};

/**
 * The `toCurrent` leg uses `reference` as both its baseline and divisor.
 * The `toOther` leg supplies `current` as its divisor so both variances
 * share a denominator and can be compared as percentage-point differences.
 */
const computeVarianceLeg = (
  value: number | null,
  reference: number | null,
  divisor: number | null = reference,
): number | null => {
  if (
    value === null ||
    reference === null ||
    divisor === null ||
    divisor === 0
  ) {
    return null;
  }

  return Math.round(((value - reference) / divisor) * 100);
};

export const variance = (inputs: CarrierCellInputs): CellPair => {
  if (inputs.isVirgin) return { toCurrent: null, toOther: null };

  return {
    toCurrent: computeVarianceLeg(inputs.value, inputs.current),
    toOther: computeVarianceLeg(inputs.value, inputs.other, inputs.current),
  };
};

const computeSavingsLeg = (
  value: number | null,
  reference: number | null,
): number | null => {
  if (value === null || reference === null || reference === 0) return null;

  return Math.round(value - reference);
};

export const dollarSavings = (inputs: CarrierCellInputs): CellPair => {
  if (inputs.isVirgin) return { toCurrent: null, toOther: null };

  return {
    toCurrent: computeSavingsLeg(inputs.value, inputs.current),
    toOther: computeSavingsLeg(inputs.value, inputs.other),
  };
};

/**
 * Computes a single-leg comparison for a period column against
 * current. Only meaningful for renewal and revised renewal — current
 * vs current is a no-op. Returns a CellPair with only `toCurrent`
 * populated; `toOther` is always null at the period level.
 */
const periodCellPair = (
  periods: PeriodColumns,
  periodKey: PeriodKey,
  coverageId: string,
  isVirgin: boolean,
  computeLeg: (value: number | null, reference: number | null) => number | null,
): CellPair | null => {
  if (isVirgin || periodKey === "current") return null;

  const periodPremium =
    periods[periodKey].coverages[coverageId]?.annualPremium ?? null;
  const currentPremium =
    periods.current.coverages[coverageId]?.annualPremium ?? null;

  return {
    toCurrent: computeLeg(periodPremium, currentPremium),
    toOther: null,
  };
};

export const periodVariance = (
  periods: PeriodColumns,
  periodKey: PeriodKey,
  coverageId: string,
  isVirgin: boolean,
): CellPair | null =>
  periodCellPair(periods, periodKey, coverageId, isVirgin, computeVarianceLeg);

export const periodDollarSavings = (
  periods: PeriodColumns,
  periodKey: PeriodKey,
  coverageId: string,
  isVirgin: boolean,
): CellPair | null =>
  periodCellPair(periods, periodKey, coverageId, isVirgin, computeSavingsLeg);

const hasPremium = (value: number | undefined): value is number =>
  value !== undefined && value !== 0;

const hasAnyPremium = (
  column: PeriodColumn,
  coverageIds: ReadonlySet<string>,
): boolean =>
  [...coverageIds].some((id) =>
    hasPremium(column.coverages[id]?.annualPremium),
  );

const isVisibleWithPremium = (
  column: PeriodColumn,
  coverageIds: ReadonlySet<string>,
): boolean => !column.hidden && hasAnyPremium(column, coverageIds);

/**
 * Picks the reference period for a totals block's `toOther` leg and
 * its label: revised renewal wins over renewal. A period counts only
 * when it is visible and at least one of the block's `coverageIds` has
 * a nonzero premium in it. A visible but empty period's total falls
 * back to current premiums, so comparing against it would repeat the
 * `toCurrent` leg. Null when neither period counts, in which case the
 * totals row renders only a `toCurrent` leg.
 */
const pickOtherPeriodKey = (
  periods: PeriodColumns,
  coverageIds: ReadonlySet<string>,
): PeriodKey | null => {
  if (isVisibleWithPremium(periods.revisedRenewal, coverageIds)) {
    return "revisedRenewal";
  }
  if (isVisibleWithPremium(periods.renewal, coverageIds)) return "renewal";

  return null;
};

/**
 * Resolves the trailing portion of the row label based on which
 * period columns are visible AND have a recorded premium for the
 * given coverage. Revised renewal wins over renewal per the spec.
 * Returns null when current is not visible, since the label is
 * anchored to current.
 */
const coverageReferenceSuffix = (
  periods: PeriodColumns,
  coverageId: string,
): string | null => {
  if (periods.current.hidden) return null;

  if (
    !periods.revisedRenewal.hidden &&
    periods.revisedRenewal.coverages[coverageId]?.annualPremium !== undefined
  ) {
    return "current | revised renewal";
  }

  if (
    !periods.renewal.hidden &&
    periods.renewal.coverages[coverageId]?.annualPremium !== undefined
  ) {
    return "current | renewal";
  }

  return "current";
};

/**
 * Suffix for totals rows, naming the same periods the totals cells
 * compare against (see `pickOtherPeriodKey`).
 */
const referenceSuffix = (
  periods: PeriodColumns,
  coverageIds: ReadonlySet<string>,
): string | null => {
  if (periods.current.hidden) return null;

  const otherKey = pickOtherPeriodKey(periods, coverageIds);
  if (otherKey === "revisedRenewal") return "current | revised renewal";
  if (otherKey === "renewal") return "current | renewal";

  return "current";
};

export const varianceRowLabel = (
  payload: FinancialsTableResponse,
  coverageId: string,
): string => {
  const suffix = coverageReferenceSuffix(payload.periods, coverageId);

  return suffix === null ? "" : `Variance to ${suffix}`;
};

export const dollarSavingsRowLabel = (
  payload: FinancialsTableResponse,
  coverageId: string,
): string => {
  const suffix = coverageReferenceSuffix(payload.periods, coverageId);

  return suffix === null ? "" : `Dollar Savings to ${suffix}`;
};

/**
 * Totals-row counterpart of the per-coverage labels: revised renewal
 * still wins over renewal, but each period is judged across the
 * `coverageIds` the block sums (see `referenceSuffix`) and the label
 * falls back to the bare prefix, never blank, when current is hidden.
 */
const totalsLabel = (
  payload: FinancialsTableResponse,
  coverageIds: ReadonlySet<string>,
  prefix: string,
): string => {
  const suffix = referenceSuffix(payload.periods, coverageIds);

  return suffix === null ? prefix : `${prefix} to ${suffix}`;
};

export const totalVarianceRowLabel = (
  payload: FinancialsTableResponse,
  coverageIds: ReadonlySet<string>,
): string => totalsLabel(payload, coverageIds, "Total Variance");

export const totalDollarSavingsRowLabel = (
  payload: FinancialsTableResponse,
  coverageIds: ReadonlySet<string>,
): string => totalsLabel(payload, coverageIds, "Total Dollar Savings");

const sumColumnValues = (
  column: PeriodColumn | CarrierColumn,
  coverageIds: ReadonlySet<string>,
): number =>
  [...coverageIds].reduce(
    (sum, id) => sum + (column.coverages[id]?.annualPremium ?? 0),
    0,
  );

/**
 * A missing or zero premium falls back, in order, to the visible periods
 * after the column's own position in this chain: a carrier column tries
 * all three, revised renewal tries renewal then current, renewal tries
 * current, and current never falls back.
 */
const PREMIUM_FALLBACK_CHAIN: readonly PeriodKey[] = [
  "revisedRenewal",
  "renewal",
  "current",
];

const premiumWithFallback = (
  column: PeriodColumn | CarrierColumn,
  periods: PeriodColumns,
  fallbackKeys: readonly PeriodKey[],
  coverageId: string,
): number => {
  const own = column.coverages[coverageId]?.annualPremium;
  if (hasPremium(own)) return own;

  const fallback = fallbackKeys
    .filter((key) => !periods[key].hidden)
    .map((key) => periods[key].coverages[coverageId]?.annualPremium)
    .find(hasPremium);

  return fallback ?? 0;
};

const sumColumnValuesWithFallback = (
  column: PeriodColumn | CarrierColumn,
  periods: PeriodColumns,
  fallbackKeys: readonly PeriodKey[],
  coverageIds: ReadonlySet<string>,
): number =>
  [...coverageIds].reduce(
    (sum, id) => sum + premiumWithFallback(column, periods, fallbackKeys, id),
    0,
  );

const periodFallbackKeys = (period: PeriodKey): readonly PeriodKey[] =>
  PREMIUM_FALLBACK_CHAIN.slice(PREMIUM_FALLBACK_CHAIN.indexOf(period) + 1);

export const totalAnnualPremiumForPeriod = (
  payload: FinancialsTableResponse,
  period: PeriodKey,
  coverageIds: ReadonlySet<string>,
): number =>
  sumColumnValuesWithFallback(
    payload.periods[period],
    payload.periods,
    periodFallbackKeys(period),
    coverageIds,
  );

export const totalAnnualPremiumForCarrier = (
  payload: FinancialsTableResponse,
  carrierId: string,
  coverageIds: ReadonlySet<string>,
): number | null => {
  const carrier = payload.carriers.find((c) => c.id === carrierId);
  if (!carrier) return null;

  return sumColumnValuesWithFallback(
    carrier,
    payload.periods,
    PREMIUM_FALLBACK_CHAIN,
    coverageIds,
  );
};

/**
 * A totals block's premium net of its credit for one column. A column the
 * block holds no credit for nets nothing off, so the row repeats the total
 * annual premium.
 */
export const totalPremiumWithCredits = (
  totalPremium: number | null,
  credit: number | null | undefined,
): number | null =>
  totalPremium === null ? null : totalPremium - (credit ?? 0);

const totalsCellPair = (
  carrierTotal: number,
  payload: FinancialsTableResponse,
  coverageIds: ReadonlySet<string>,
  computeLeg: (
    value: number,
    reference: number | null,
    divisor?: number | null,
  ) => number | null,
): CellPair => {
  const currentTotal = sumColumnValues(payload.periods.current, coverageIds);
  const otherKey = pickOtherPeriodKey(payload.periods, coverageIds);
  const otherTotal =
    otherKey === null
      ? null
      : totalAnnualPremiumForPeriod(payload, otherKey, coverageIds);

  // `toOther` passes `currentTotal` as the divisor explicitly — see
  // computeVarianceLeg. computeSavingsLeg ignores the extra argument.
  return {
    toCurrent: computeLeg(carrierTotal, currentTotal),
    toOther: computeLeg(carrierTotal, otherTotal, currentTotal),
  };
};

export const totalVarianceForCarrier = (
  payload: FinancialsTableResponse,
  carrierId: string,
  coverageIds: ReadonlySet<string>,
): CellPair | null => {
  const carrierTotal = totalAnnualPremiumForCarrier(
    payload,
    carrierId,
    coverageIds,
  );
  if (carrierTotal === null) return null;

  return totalsCellPair(carrierTotal, payload, coverageIds, computeVarianceLeg);
};

export const totalDollarSavingsForCarrier = (
  payload: FinancialsTableResponse,
  carrierId: string,
  coverageIds: ReadonlySet<string>,
): CellPair | null => {
  const carrierTotal = totalAnnualPremiumForCarrier(
    payload,
    carrierId,
    coverageIds,
  );
  if (carrierTotal === null) return null;

  return totalsCellPair(carrierTotal, payload, coverageIds, computeSavingsLeg);
};

/**
 * Period-column totals comparison against current: the `periodCellPair`
 * rule at totals scale. A single `toCurrent` leg over the summed
 * coverages, `toOther` always null. Null for the current column, where
 * a self-comparison is meaningless.
 */
const totalsPeriodCellPair = (
  payload: FinancialsTableResponse,
  period: PeriodKey,
  coverageIds: ReadonlySet<string>,
  computeLeg: (value: number, reference: number | null) => number | null,
): CellPair | null => {
  if (period === "current") return null;

  const periodTotal = totalAnnualPremiumForPeriod(payload, period, coverageIds);
  const currentTotal = sumColumnValues(payload.periods.current, coverageIds);

  return {
    toCurrent: computeLeg(periodTotal, currentTotal),
    toOther: null,
  };
};

export const totalVarianceForPeriod = (
  payload: FinancialsTableResponse,
  period: PeriodKey,
  coverageIds: ReadonlySet<string>,
): CellPair | null =>
  totalsPeriodCellPair(payload, period, coverageIds, computeVarianceLeg);

export const totalDollarSavingsForPeriod = (
  payload: FinancialsTableResponse,
  period: PeriodKey,
  coverageIds: ReadonlySet<string>,
): CellPair | null =>
  totalsPeriodCellPair(payload, period, coverageIds, computeSavingsLeg);
